import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { apiGet, apiSend } from '../api/client';
import { useAuth } from '../auth/AuthContext';

interface Note {
  id: number;
  event_type: string;
  title_key: string;
  body_key: string;
  title?: string | null;
  body?: string | null;
  cta_path?: string | null;
  kind?: string | null;
  is_read: boolean;
  created_at: string;
}

export function NotificationsPage() {
  const { t } = useTranslation(['common']);
  const { user } = useAuth();
  const [items, setItems] = useState<Note[]>([]);
  const [optIn, setOptIn] = useState(true);

  async function reload() {
    const notes = await apiGet<Note[]>('/api/v1/notifications');
    setItems(notes);
    const prefs = await apiGet<{ marketing_opt_in: boolean }>('/api/v1/notifications/preferences');
    setOptIn(prefs.marketing_opt_in);
  }

  useEffect(() => {
    if (!user) return;
    void reload().catch(() => setItems([]));
  }, [user?.public_id]);

  if (!user) {
    return (
      <div className="page">
        <h1>{t('common:notificationsTitle', { defaultValue: 'Notifications' })}</h1>
        <Link to="/auth/login">{t('common:login')}</Link>
      </div>
    );
  }

  return (
    <div className="page">
      <h1>{t('common:notificationsTitle', { defaultValue: 'Notifications' })}</h1>
      <div className="actions" style={{ marginBottom: 16 }}>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => void apiSend('POST', '/api/v1/notifications/read-all').then(reload)}
        >
          {t('common:markAllRead', { defaultValue: 'Mark all as read' })}
        </button>
      </div>
      <label className="check-row">
        <input
          type="checkbox"
          checked={optIn}
          onChange={(e) => {
            const next = e.target.checked;
            setOptIn(next);
            void apiSend('PUT', '/api/v1/notifications/preferences', { marketing_opt_in: next });
          }}
        />
        {t('common:marketingOptIn', { defaultValue: 'Receive promotional notifications' })}
      </label>
      {items.length === 0 ? (
        <div className="state-card">
          <h3>{t('common:emptyNotifications', { defaultValue: 'No notifications' })}</h3>
          <p>
            {t('common:notificationsUnavailable', {
              defaultValue: 'Event notifications appear here when generated. Nothing is fabricated.',
            })}
          </p>
        </div>
      ) : (
        <div className="history-list">
          {items.map((n) => (
            <article key={n.id} className={`history-row ${n.is_read ? '' : 'is-unread'}`}>
              <button
                type="button"
                className="linkish"
                onClick={() => {
                  if (!n.is_read) void apiSend('POST', `/api/v1/notifications/${n.id}/read`).then(reload);
                }}
              >
                <strong>{n.title || n.title_key}</strong>
                <span>{n.created_at}</span>
                <p>{n.body || n.body_key}</p>
                {n.kind ? <small>{n.kind}</small> : null}
              </button>
              {n.cta_path ? (
                <Link to={n.cta_path.startsWith('/') ? n.cta_path : '/'}>{t('common:open', { defaultValue: 'Open' })}</Link>
              ) : null}
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
